import { validatePostpaidForm, validateFieldChange } from './PostpaidValidation';
import { getLocationsByRegion, getLocationsByExchangeArea, getLocationsByChargeArea } from '../../../../data/vobbLocationData';

export const handleInputChange = (formData, setFormData, setIsBlankSimActive) => (event, section, item) => {
  let name, value;
  if (event && event.target) {
    // If it's an event object
    ({ name, value } = event.target);
    console.log(`Section: ${section}, Item: ${item}, Field: ${name}, Value: ${value}`);
  } else {
    name = item;
    value = event;
    console.log(`Section: ${section}, Item: ${item}, Field: ${name}, Value: ${value}`);
  }

  setFormData(prevData => {
    let updatedData = {
      ...prevData,
      [section]: {
        ...prevData[section],
        [item]: section === 'general' ? value : {
          ...prevData[section][item],
          [name]: value
        }
      }
    };

    // Bidirectional auto-fill for VOBB
    if (section === 'Postpaid' && item === 'VOBB') {
      if (name === 'region' && value) {
        // Region selected -> auto-fill exchange_area and charge_area
        const location = getLocationsByRegion(value);
        if (location && location.exchangeArea && location.chargeArea) {
          updatedData.Postpaid.VOBB = {
            ...updatedData.Postpaid.VOBB,
            exchange_area: location.exchangeArea,
            charge_area: location.chargeArea
          };
          console.log(`Auto-filled exchange_area: ${location.exchangeArea}, charge_area: ${location.chargeArea} from region: ${value}`);
        }
      } else if (name === 'exchange_area' && value) {
        // Exchange area selected -> auto-fill region and charge_area
        const location = getLocationsByExchangeArea(value);
        if (location && location.region && location.chargeArea) {
          updatedData.Postpaid.VOBB = {
            ...updatedData.Postpaid.VOBB,
            region: location.region,
            charge_area: location.chargeArea
          };
          console.log(`Auto-filled region: ${location.region}, charge_area: ${location.chargeArea} from exchange_area: ${value}`);
        }
      } else if (name === 'charge_area' && value) {
        // Charge area selected -> auto-fill region and exchange_area (first match)
        const location = getLocationsByChargeArea(value);
        if (location && location.region && location.exchangeArea) {
          updatedData.Postpaid.VOBB = {
            ...updatedData.Postpaid.VOBB,
            region: location.region,
            exchange_area: location.exchangeArea
          };
          console.log(`Auto-filled region: ${location.region}, exchange_area: ${location.exchangeArea} from charge_area: ${value}`);
        }
      }
    }

    // Check which items are filled based on their requirements
    const checkFieldsFilled = (data) => {
      if (!data.Postpaid) return false;
      
      const isItemFilled = (itemName, itemData) => {
        if (!itemData) return false;
        
        // Helper function to check if a field has valid content (including custom values)
        const hasValidContent = (field) => {
          return field != null && String(field).trim() !== '';
        };

        switch(itemName) {
          case 'eSIM':
            // eSIM requires all three fields - also work with custom entries
            return hasValidContent(itemData.store_id) &&
                   hasValidContent(itemData.material_code) &&
                   hasValidContent(itemData.quantity);
          case 'MSISDN':
            // MSISDN only needs store_id and quantity - also work with custom entries
            return hasValidContent(itemData.store_id) &&
                   hasValidContent(itemData.quantity);
          case 'VOBB':
            // VOBB needs region, exchange_area, charge_area, and quantity
            return hasValidContent(itemData.region) &&
                   hasValidContent(itemData.exchange_area) &&
                   hasValidContent(itemData.charge_area) &&
                   hasValidContent(itemData.quantity);
          default:
            return false;
        }
      };

      // Check each item and log the status
      const filledItems = ['MSISDN', 'eSIM', 'VOBB'].filter(itemName => {
        const isFilled = isItemFilled(itemName, data.Postpaid[itemName]);
        console.log(`${itemName} filled status:`, isFilled, data.Postpaid[itemName]);
        return isFilled;
      });

      return filledItems.length > 0;
    };

    // Check if any field is completely filled according to its requirements
    const hasOtherFieldsFilled = checkFieldsFilled(updatedData);
    console.log('Has other fields filled:', hasOtherFieldsFilled);
    
    // Always update isBlankSimActive state based on other fields
    if (hasOtherFieldsFilled) {
      console.log('Disabling Blank Sim...');
      setIsBlankSimActive(false);
      // Clear Blank Sim data
      updatedData.Postpaid['Blank Sim'] = {
        store_id: '',
        material_code: '',
        quantity: ''
      };
      console.log("Cleared and disabled Blank Sim because another item is completely filled");
    } else if (section === 'Postpaid') {
      if (item === 'Blank Sim') {
        // Handle Blank Sim changes
        const blankSimData = updatedData.Postpaid['Blank Sim'];
        // Helper function to check if a field has valid content (including custom values)
        const hasValidContent = (field) => {
          return field != null && String(field).trim() !== '';
        };

        const isFilled = hasValidContent(blankSimData.store_id) && 
                        hasValidContent(blankSimData.material_code) && 
                        hasValidContent(blankSimData.quantity);
        
        console.log('Blank Sim filled status:', isFilled);
        
        if (isFilled) {
          // Clear other fields if Blank Sim is filled
          ['MSISDN', 'eSIM'].forEach(otherItem => {
            updatedData.Postpaid[otherItem] = {
              store_id: '',
              material_code: '',
              quantity: ''
            };
          });
          // Clear VOBB with its specific fields
          updatedData.Postpaid.VOBB = {
            region: '',
            exchange_area: '',
            charge_area: '',
            quantity: ''
          };
        }
        setIsBlankSimActive(isFilled);
      } else {
        // Handle MSISDN, eSIM, or VOBB changes
        // Helper function to check if a field has valid content (including custom values)
        const hasValidContent = (field) => {
          return field != null && String(field).trim() !== '';
        };

        let isCurrentItemFilled;
        if (item === 'eSIM') {
          isCurrentItemFilled = hasValidContent(updatedData.Postpaid[item].store_id) &&
                                hasValidContent(updatedData.Postpaid[item].material_code) &&
                                hasValidContent(updatedData.Postpaid[item].quantity);
        } else if (item === 'VOBB') {
          isCurrentItemFilled = hasValidContent(updatedData.Postpaid[item].region) &&
                                hasValidContent(updatedData.Postpaid[item].exchange_area) &&
                                hasValidContent(updatedData.Postpaid[item].charge_area) &&
                                hasValidContent(updatedData.Postpaid[item].quantity);
        } else {
          isCurrentItemFilled = hasValidContent(updatedData.Postpaid[item].store_id) &&
                                hasValidContent(updatedData.Postpaid[item].quantity);
        }
        
        console.log(`${item} filled status:`, isCurrentItemFilled);
        
        if (isCurrentItemFilled) {
          setIsBlankSimActive(false);
          updatedData.Postpaid['Blank Sim'] = {
            store_id: '',
            material_code: '',
            quantity: ''
          };
        }
      }
    }

    return updatedData;
  });
};

export const handleNewFieldChange = (newFields, setNewFields, setErrors, isBlankSimActive) => (key, fieldName, value) => {
  // Check if the field should be disabled
  const field = newFields.find(f => f.key === key);
  const isDisabled = isBlankSimActive && field.section !== 'Blank Sim' && field.item !== 'MSISDN' && field.item !== 'eSIM' && field.item !== 'VOBB';

  if (isDisabled) {
    return; // Don't update the field if it's disabled
  }

  // Update the field value
  setNewFields(prevFields => {
    const updatedFields = prevFields.map(field => {
      if (field.key === key) {
        if (fieldName === 'quantity' && value && !/^\d*$/.test(value)) {
          return field; // Don't update if quantity is not a valid number
        }
        return { ...field, [fieldName]: value };
      }
      return field;
    });
    return updatedFields;
  });

  // Handle validation
  if (fieldName === 'quantity') {
    const error = validateFieldChange(fieldName, value, field.item);
    if (error) {
      setErrors(prev => ({
        ...prev,
        newFields: {
          ...prev.newFields,
          [key]: { ...prev.newFields?.[key], [fieldName]: error }
        }
      }));
    } else {
      setErrors(prev => ({
        ...prev,
        newFields: {
          ...prev.newFields,
          [key]: { ...prev.newFields?.[key], [fieldName]: '' }
        }
      }));
    }
  }
};

export const clearFormData = (formData, setFormData, isBlankSimDataFilled) => () => {
  setFormData(prevData => {
    const clearedData = { ...prevData };
    // Store Blank Sim data before clearing
    const blankSimData = clearedData.Postpaid && clearedData.Postpaid['Blank Sim'];

    // Clear all sections except general
    Object.keys(clearedData).forEach(section => {
      if (section !== 'general') {
        Object.keys(clearedData[section]).forEach(item => {
          if (section === 'Postpaid' && item === 'Blank Sim' && isBlankSimDataFilled({ Postpaid: { 'Blank Sim': blankSimData } })) {
            // Skip clearing Blank Sim data if it was filled
            return;
          }

          clearedData[section][item] = {
            store_id: "",
            material_code: "",
            quantity: ""
          };
        });
      }
    });

    // Restore Blank Sim data if it was filled
    if (isBlankSimDataFilled({ Postpaid: { 'Blank Sim': blankSimData } })) {
      clearedData.Postpaid['Blank Sim'] = blankSimData;
    }

    return clearedData;
  });
};
